import { IMasterDataAdapter } from './IMasterDataAdapter';
import { ApiResponse } from '../../types/api';
import { Plant, Building, Floor, Location, Department, Category, Subcategory, PriorityMaster, StatusMaster } from '../../types/masterData';
import { ApiClient } from '../apiClient';
import { hasApiEndpoint } from '../../config/backend';

const MOCK_PLANTS: Plant[] = [
  { plant_id: 'PLT-001', name: 'Main Manufacturing Plant 1', code: 'PLANT1' },
  { plant_id: 'PLT-002', name: 'Assembly Unit Plant 2', code: 'PLANT2' },
];

const MOCK_BUILDINGS: Building[] = [
  { building_id: 'BLD-001', plant_id: 'PLT-001', name: 'Block A - Admin & Operations' },
  { building_id: 'BLD-002', plant_id: 'PLT-001', name: 'Block B - Production Floor' },
  { building_id: 'BLD-003', plant_id: 'PLT-002', name: 'Assembly Hall 1' },
];

const MOCK_FLOORS: Floor[] = [
  { floor_id: 'FLR-001', building_id: 'BLD-001', name: 'Ground Floor' },
  { floor_id: 'FLR-002', building_id: 'BLD-001', name: 'First Floor' },
  { floor_id: 'FLR-003', building_id: 'BLD-002', name: 'Main Shop Floor' },
];

const MOCK_LOCATIONS: Location[] = [
  { location_id: 'LOC-001', floor_id: 'FLR-001', name: 'Server Room 101', code: 'SR-101' },
  { location_id: 'LOC-002', floor_id: 'FLR-001', name: 'HR Department Area', code: 'HR-1' },
  { location_id: 'LOC-003', floor_id: 'FLR-003', name: 'CNC Machine Bay 4', code: 'CNC-4' },
];

const MOCK_DEPARTMENTS: Department[] = [
  { department_id: 'DEP-001', name: 'Production & Manufacturing', code: 'PROD' },
  { department_id: 'DEP-002', name: 'Quality Control (QC)', code: 'QC' },
  { department_id: 'DEP-003', name: 'Quality Assurance (QA)', code: 'QA' },
  { department_id: 'DEP-004', name: 'Facilities & Maintenance', code: 'MAINT' },
  { department_id: 'DEP-005', name: 'Information Technology (IT)', code: 'IT' },
  { department_id: 'DEP-006', name: 'Human Resources (HR)', code: 'HR' },
];

const MOCK_CATEGORIES: Category[] = [
  { category_id: 'CAT-001', name: 'Air Conditioning & HVAC', code: 'AC' },
  { category_id: 'CAT-002', name: 'Electrical & Power Supply', code: 'ELEC' },
  { category_id: 'CAT-003', name: 'Plumbing & Drainage', code: 'PLUMB' },
  { category_id: 'CAT-004', name: 'Civil & Carpentry', code: 'CIVIL' },
  { category_id: 'CAT-005', name: 'Elevator & Lift Systems', code: 'LIFT' },
];

const MOCK_SUBCATEGORIES: Subcategory[] = [
  { subcategory_id: 'SUB-001', category_id: 'CAT-001', name: 'Cooling Failure / Water Leakage' },
  { subcategory_id: 'SUB-002', category_id: 'CAT-001', name: 'Compressor Noise' },
  { subcategory_id: 'SUB-003', category_id: 'CAT-002', name: 'Power Failure / Circuit Breaker' },
  { subcategory_id: 'SUB-004', category_id: 'CAT-003', name: 'Pipe Leakage / Pipe Burst' },
];

const MOCK_PRIORITIES: PriorityMaster[] = [
  { priority_id: 'PRI-001', name: 'Low', sla_hours: 72, warning_threshold_pct: 90, color_code: '#38bdf8' },
  { priority_id: 'PRI-002', name: 'Medium', sla_hours: 24, warning_threshold_pct: 85, color_code: '#fbbf24' },
  { priority_id: 'PRI-003', name: 'High', sla_hours: 8, warning_threshold_pct: 80, color_code: '#f87171' },
  { priority_id: 'PRI-004', name: 'Critical', sla_hours: 4, warning_threshold_pct: 75, color_code: '#e11d48' },
];

const MOCK_STATUSES: StatusMaster[] = [
  { status_id: 'STA-001', name: 'New', stage: 'INITIAL', color_code: '#38bdf8' },
  { status_id: 'STA-002', name: 'Under Review', stage: 'WORKFLOW', color_code: '#fbbf24' },
  { status_id: 'STA-003', name: 'Assigned', stage: 'WORKFLOW', color_code: '#c084fc' },
  { status_id: 'STA-004', name: 'In Progress', stage: 'WORKFLOW', color_code: '#a3e635' },
  { status_id: 'STA-005', name: 'Completed', stage: 'WORKFLOW', color_code: '#34d399' },
  { status_id: 'STA-006', name: 'Verified', stage: 'WORKFLOW', color_code: '#10b981' },
  { status_id: 'STA-007', name: 'Closed', stage: 'FINAL', color_code: '#64748b' },
];

export class GASMasterDataAdapter implements IMasterDataAdapter {
  async getPlants(): Promise<ApiResponse<Plant[]>> {
    if (!hasApiEndpoint()) return { success: true, data: MOCK_PLANTS, message: 'Loaded Plants' };
    return ApiClient.get<Plant[]>('/master/plants');
  }

  async getBuildings(plantId?: string): Promise<ApiResponse<Building[]>> {
    if (!hasApiEndpoint()) {
      const filtered = plantId ? MOCK_BUILDINGS.filter(b => b.plant_id === plantId) : MOCK_BUILDINGS;
      return { success: true, data: filtered, message: 'Loaded Buildings' };
    }
    return ApiClient.get<Building[]>(`/master/buildings?plantId=${plantId || ''}`);
  }

  async getFloors(buildingId?: string): Promise<ApiResponse<Floor[]>> {
    if (!hasApiEndpoint()) {
      const filtered = buildingId ? MOCK_FLOORS.filter(f => f.building_id === buildingId) : MOCK_FLOORS;
      return { success: true, data: filtered, message: 'Loaded Floors' };
    }
    return ApiClient.get<Floor[]>(`/master/floors?buildingId=${buildingId || ''}`);
  }

  async getLocations(floorId?: string): Promise<ApiResponse<Location[]>> {
    if (!hasApiEndpoint()) {
      const filtered = floorId ? MOCK_LOCATIONS.filter(l => l.floor_id === floorId) : MOCK_LOCATIONS;
      return { success: true, data: filtered, message: 'Loaded Locations' };
    }
    return ApiClient.get<Location[]>(`/master/locations?floorId=${floorId || ''}`);
  }

  async getDepartments(): Promise<ApiResponse<Department[]>> {
    if (!hasApiEndpoint()) return { success: true, data: MOCK_DEPARTMENTS, message: 'Loaded Departments' };
    return ApiClient.get<Department[]>('/master/departments');
  }

  async getCategories(): Promise<ApiResponse<Category[]>> {
    if (!hasApiEndpoint()) return { success: true, data: MOCK_CATEGORIES, message: 'Loaded Categories' };
    return ApiClient.get<Category[]>('/master/categories');
  }

  async getSubcategories(categoryId?: string): Promise<ApiResponse<Subcategory[]>> {
    if (!hasApiEndpoint()) {
      const filtered = categoryId ? MOCK_SUBCATEGORIES.filter(s => s.category_id === categoryId) : MOCK_SUBCATEGORIES;
      return { success: true, data: filtered, message: 'Loaded Subcategories' };
    }
    return ApiClient.get<Subcategory[]>(`/master/subcategories?categoryId=${categoryId || ''}`);
  }

  async getPriorities(): Promise<ApiResponse<PriorityMaster[]>> {
    if (!hasApiEndpoint()) return { success: true, data: MOCK_PRIORITIES, message: 'Loaded Priorities' };
    return ApiClient.get<PriorityMaster[]>('/master/priorities');
  }

  async getStatuses(): Promise<ApiResponse<StatusMaster[]>> {
    if (!hasApiEndpoint()) return { success: true, data: MOCK_STATUSES, message: 'Loaded Statuses' };
    return ApiClient.get<StatusMaster[]>('/master/statuses');
  }
}
